import React, { useEffect, useState } from 'react'
import {
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography
} from '@mui/material'
import { AddCircle, Cancel, DeleteForever, Save } from '@mui/icons-material'
import { red } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { TrainingWorkoutInterface } from '../../../models/interfaces/training-workout.interface'
import { Setup } from '../../../models/interfaces/setup.interface'
import { Thumbnail } from '../../Thumbnail'
import { showMessage } from '../../../redux/reducers/snackbar/snackbar.actions'
import {
  removeSubstituteFromParent,
  updateSubstituteSetup,
  updateSubstituteSetupInState
} from '../../../redux/reducers/training-workout/training-workout.actions'
import { selectedTrainingWorkoutType } from '../../../redux/reducers/training-workout/training-workout.selectors'
import { modalAddFieldsData } from '../../../redux/reducers/modal-add-fields/modal-add-fields.selectors'
import { showModalAddFields } from '../../../redux/reducers/modal-add-fields/modal-add-fields.actions'
import { getNameByField } from './Utils'
import { Summary } from './Summary'
import { getRemoveURL } from '../functions'
import { TrainingWorkoutTypeEnum } from '../index'
import api from '../../../services/api'
import useResponsive from '../../../hooks/useResponsive'

type Props = {
  parentId: string
  substitutes: TrainingWorkoutInterface[]
  isGrouped?: boolean
}

export function SubstituteList ({ parentId, substitutes, isGrouped = false }: Props) {
  const dispatch = useAppDispatch()
  const trainingWorkoutType = useAppSelector(selectedTrainingWorkoutType)
  const theme = useTheme()
  const { t } = useTranslation()

  const handleRemove = async (substituteId: string) => {
    try {
      await api.delete(getRemoveURL(substituteId, trainingWorkoutType))
      dispatch(removeSubstituteFromParent({ parentId, substituteId }))
      dispatch(showMessage({ message: `${t('trainingWorkout.substituteRemoved')}` }))
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  return (
    <Stack mt={1} ml={2} spacing={1}>
      <Divider />
      <Typography fontSize={12} color={theme.palette.grey[500]} sx={{ mt: 0.5 }}>
        {t('trainingWorkout.substitutes')}
      </Typography>
      {substitutes.map(sub => (
        <SubstituteRow
          key={sub.id}
          substitute={sub}
          parentId={parentId}
          isGrouped={isGrouped}
          onRemove={handleRemove}
        />
      ))}
    </Stack>
  )
}

type RowProps = {
  substitute: TrainingWorkoutInterface
  parentId: string
  isGrouped: boolean
  onRemove: (id: string) => Promise<void>
}

function SubstituteRow ({ substitute, parentId, isGrouped, onRemove }: RowProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [isEditFormVisible, setIsEditFormVisible] = useState(false)

  const toggleEditForm = () => setIsEditFormVisible(prev => !prev)

  return (
    <Stack
      direction="column"
      sx={{
        background: theme.palette.grey[700],
        borderRadius: 1,
        p: 1
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Thumbnail data={substitute.workout} />
          <Typography fontSize={13} color={theme.palette.grey[400]}>
            {t(`workouts.${substitute.workout.name}`, { defaultValue: substitute.workout.name })}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={async () => await onRemove(substitute.id)}
          title={`${t('trainingWorkout.removeSubstitute')}`}
        >
          <DeleteForever sx={{ color: theme.palette.error.main }} fontSize="small" />
        </IconButton>
      </Stack>
      {!isGrouped && (
        <>
          <Divider sx={{ my: 0.5 }} />
          {isEditFormVisible
            ? <SubstituteSetupForm substitute={substitute} parentId={parentId} onClose={toggleEditForm} />
            : <Summary setup={substitute.setup} onPress={toggleEditForm} />
          }
        </>
      )}
    </Stack>
  )
}

type SetupFormProps = {
  substitute: TrainingWorkoutInterface
  parentId: string
  onClose: () => void
}

function SubstituteSetupForm ({ substitute, parentId, onClose }: SetupFormProps) {
  const dispatch = useAppDispatch()
  const trainingWorkoutType = useAppSelector(selectedTrainingWorkoutType)
  const addFieldsData = useAppSelector(modalAddFieldsData)
  const theme = useTheme()
  const mdUp = useResponsive('up', 'md')
  const [newSetup, setNewSetup] = useState<Setup[]>(substitute.setup)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (addFieldsData && addFieldsData.id === substitute.id) {
      setNewSetup(addFieldsData.setup)
    }
  }, [addFieldsData])

  const handleOnChange = (value: string, field: string) => {
    setNewSetup(prev => prev.map(s => s.field === field ? { ...s, value } : s))
  }

  const handleSaveSetup = async () => {
    try {
      setIsSaving(true)
      await dispatch(updateSubstituteSetup({
        payload: { setup: newSetup },
        trainingWorkoutId: substitute.id,
        type: trainingWorkoutType
      }))
      dispatch(updateSubstituteSetupInState({ parentId, substituteId: substitute.id, setup: newSetup }))
      onClose()
      dispatch(showMessage({ message: 'Salvo com sucesso' }))
    } catch {
      dispatch(showMessage({ message: 'Erro ao salvar registro', type: 'error' }))
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemoveField = async (field: string) => {
    try {
      const filtered = newSetup.filter(item => item.field !== field)
      await dispatch(updateSubstituteSetup({
        payload: { setup: filtered },
        trainingWorkoutId: substitute.id,
        type: trainingWorkoutType
      }))
      dispatch(updateSubstituteSetupInState({ parentId, substituteId: substitute.id, setup: filtered }))
      dispatch(showMessage({ message: 'Salvo com sucesso' }))
      setNewSetup(filtered)
    } catch {
      dispatch(showMessage({ message: 'Erro ao excluir registro', type: 'error' }))
    }
  }

  const handleShowAddModal = () => {
    dispatch(showModalAddFields({
      data: { setup: newSetup, id: substitute.id, workoutType: substitute.workout.type },
      isVisible: true
    }))
  }

  return (
    <Stack p={1} mt={0.5}>
      <Stack direction={(mdUp ? 'row' : 'column')} alignItems="stretch">
        {newSetup.map((item: Setup) => (
          <Stack key={item.field} mr={2} justifyContent="end">
            <Stack>
              <span style={{
                color: theme.palette.grey[300],
                marginLeft: 1,
                marginBottom: 2,
                fontSize: 14,
                paddingBottom: 8,
                paddingLeft: 2,
                paddingRight: 2
              }}>
                {getNameByField(item.field)}
              </span>
            </Stack>
            <Stack pb={1.5}>
              <OutlinedInput
                style={{ backgroundColor: theme.palette.grey[900], borderColor: theme.palette.grey[900] }}
                size="small"
                placeholder={getNameByField(item.field)}
                value={item.value}
                onChange={e => handleOnChange(e.target.value, item.field)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      title="Excluir campo"
                      aria-label={getNameByField(item.field)}
                      onClick={async () => await handleRemoveField(item.field)}
                      style={{ marginTop: -40, marginRight: -25, zIndex: 1 }}
                    >
                      <Cancel sx={{ color: red[900] }} />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Stack>
          </Stack>
        ))}
        <Stack mt={2} direction="row" alignItems="center" justifyItems="center" justifyContent="center">
          <Stack>
            <IconButton color="default" onClick={handleSaveSetup} disabled={isSaving}>
              {isSaving ? <CircularProgress size={18} /> : <Save />}
            </IconButton>
          </Stack>
          <Stack>
            <IconButton onClick={handleShowAddModal}>
              <AddCircle />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
